import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCheckout } from "../hooks/useCheckout";

const API_BASE = `${process.env.REACT_APP_API_BASE_URL}`;
const DISABLE_PRINT_API_STORAGE_KEY = "uuDaiCTDisablePrintAPI";
const PAGE_STYLE_ID = "uu-dai-ct-page-style";
const MIN_DRINK_ELIGIBLE_AMOUNT = 20000;

const getProductKey = (maSP) => String(maSP);
const createBillKey = (prefix, maSP) =>
  `${prefix}_${getProductKey(maSP)}_${Date.now()}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
const isBreadLine = (item) => !item.isPromo && !item.isAddon;
const sumDrinkSelections = (selections) =>
  Object.values(selections).reduce(
    (sum, quantity) => sum + (Number(quantity) || 0),
    0
  );
const PAYMENT_METHOD_OPTIONS = [
  { value: "cash", label: "Tiền mặt", icon: "💵" },
  { value: "transfer", label: "Chuyển khoản", icon: "🏦" },
];
const SEARCH_KEYBOARD_ROWS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["z", "x", "c", "v", "b", "n", "m"],
];
const normalizeSearchText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .trim();

function UuDaiCT() {
  const productGridRef = useRef(null);
  const searchInputRef = useRef(null);
  const [maCH] = useState(() => localStorage.getItem("IDCH") || "157");
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [drinkOptions, setDrinkOptions] = useState([]);
  const [addonOptions, setAddonOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [drinkSelections, setDrinkSelections] = useState({});
  const [paramMaUD, setParamMaUD] = useState(null);
  const [lastFetchedDrinkId, setLastFetchedDrinkId] = useState(null);
  const [billItems, setBillItems] = useState([]);
  const [showDrinkModal, setShowDrinkModal] = useState(false);
  const [tempDrinkSelections, setTempDrinkSelections] = useState({});
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [selectedBreadForAddon, setSelectedBreadForAddon] = useState(null);
  const [tempAddonSelections, setTempAddonSelections] = useState({});
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [cashGiven, setCashGiven] = useState("");
  const [billNote, setBillNote] = useState("");
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showIncompleteDrinkWarning, setShowIncompleteDrinkWarning] =
    useState(false);
  const [showTransferQRZoom, setShowTransferQRZoom] = useState(false);
  const [productFilter, setProductFilter] = useState("banhmi"); // Default to "banhmi"
  const [productSearch, setProductSearch] = useState("");
  const [showSearchKeyboard, setShowSearchKeyboard] = useState(false);
  const [productGridColumnCount, setProductGridColumnCount] = useState(1);
  const [isDisablePrintAPI, setIsDisablePrintAPI] = useState(() => {
    return localStorage.getItem(DISABLE_PRINT_API_STORAGE_KEY) === "true";
  });

  const { isProcessing, processCheckout } = useCheckout();

  const buyBreadGetDrink = useMemo(
    () => promotions.filter((p) => p.MXTY === 2),
    [promotions]
  );

  useEffect(() => {
    if (
      !PAYMENT_METHOD_OPTIONS.some((method) => method.value === paymentMethod)
    ) {
      setPaymentMethod("cash");
    }
  }, [paymentMethod]);

  // Filter products based on selected filter
  const filteredPromoProducts = useMemo(() => {
    if (!selectedPromotion || !selectedPromotion.items) return [];

    const normalizedQuery = normalizeSearchText(productSearch);

    return selectedPromotion.items.filter((item) => {
      const tenSP = item.Ten_SP || "";
      let matchesCategory = true;

      if (productFilter === "banhmi") {
        matchesCategory =
          tenSP.startsWith("Bánh mì") || tenSP.startsWith("Bánh mỳ");
      } else if (productFilter === "burger") {
        matchesCategory =
          tenSP.startsWith("Hamburger") || tenSP.startsWith("Hambuger");
      }

      if (!matchesCategory) return false;
      if (!normalizedQuery) return true;

      const price = Number(item.Gia || item.DonGia) || 0;
      const searchableProductText = normalizeSearchText(
        `${tenSP} ${price} ${price.toLocaleString("vi-VN")}đ`
      );
      const normalizedPriceQuery = normalizedQuery
        .replace(/[.,\s]/g, "")
        .replace(/d$/, "");
      const matchesPrice =
        /^[\d.,\s]+d?$/.test(normalizedQuery) &&
        normalizedPriceQuery.length > 0 &&
        String(price).includes(normalizedPriceQuery);

      return (
        searchableProductText.includes(normalizedQuery) || matchesPrice
      );
    });
  }, [selectedPromotion, productFilter, productSearch]);

  const handleSearchKeyboardKey = (key) => {
    setProductSearch((previousValue) => {
      if (key === "backspace") {
        return Array.from(previousValue).slice(0, -1).join("");
      }
      if (key === "clear") return "";
      if (key === "space") return `${previousValue} `;
      return `${previousValue}${key}`;
    });

    window.requestAnimationFrame(() => searchInputRef.current?.focus());
  };

  const handleToggleSearchKeyboard = () => {
    const nextValue = !showSearchKeyboard;
    setShowSearchKeyboard(nextValue);

    if (nextValue) {
      window.requestAnimationFrame(() => searchInputRef.current?.focus());
    }
  };

  useEffect(() => {
    if (!selectedPromotion) return;

    const grid = productGridRef.current;
    if (!grid) return;

    const updateColumnCount = () => {
      const gridStyles = window.getComputedStyle(grid);
      const columns = gridStyles.gridTemplateColumns
        .split(" ")
        .filter((column) => column && column !== "none").length;

      setProductGridColumnCount(Math.max(1, columns));
    };

    updateColumnCount();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateColumnCount);
      return () => window.removeEventListener("resize", updateColumnCount);
    }

    const observer = new ResizeObserver(updateColumnCount);
    observer.observe(grid);

    return () => observer.disconnect();
  }, [selectedPromotion, productFilter, filteredPromoProducts.length]);

  const fetchPromotions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/promotions/${maCH}`);
      if (!res.ok) throw new Error("Không tải được danh sách ưu đãi");
      const data = await res.json();
      setPromotions(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || "Lỗi không xác định");
    } finally {
      setLoading(false);
    }
  };

  const fetchDrinks = async (maUD) => {
    if (!maUD) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/promotion-details/${maUD}`);
      if (!res.ok) throw new Error("Không tải được danh sách nước tặng");
      const data = await res.json();
      const items =
        Array.isArray(data) && data[0] && Array.isArray(data[0].items)
          ? data[0].items
          : [];
      setDrinkOptions(items);
      setLastFetchedDrinkId(maUD);
    } catch (err) {
      setError(err.message || "Lỗi không xác định");
    } finally {
      setLoading(false);
    }
  };

  const fetchAddonOptions = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(
        `${API_BASE}/api/san-pham?IDCH=${maCH}&MaNLSP=NLBL`
      );
      if (!res.ok) throw new Error("Không tải được danh sách món ăn kèm");
      const data = await res.json();
      const items = Array.isArray(data) ? data : [];
      setAddonOptions(items);
      return items;
    } catch (err) {
      setError(err.message || "Lỗi không xác định");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const parseMaUDFromUrl = () => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const ma = params.get("Ma");
      const num = Number(ma);
      return Number.isFinite(num) ? num : null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const urlMaUD = parseMaUDFromUrl();
    if (urlMaUD) {
      setParamMaUD(urlMaUD);
      fetchDrinks(urlMaUD);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
    fetchAddonOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (paramMaUD && buyBreadGetDrink.length) {
      const found = buyBreadGetDrink.find((p) => p.MaUD === paramMaUD);
      if (found) handleSelectPromotion(found.MaUD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramMaUD, buyBreadGetDrink]);

  const handleSelectPromotion = (maUD) => {
    const found = buyBreadGetDrink.find((p) => p.MaUD === Number(maUD));
    setSelectedPromotion(found || null);
    setDrinkSelections({});
    setTempDrinkSelections({});
    setShowAddonModal(false);
    setSelectedBreadForAddon(null);
    setTempAddonSelections({});
    setBillItems([]);
    if (found && maUD !== lastFetchedDrinkId) fetchDrinks(found.MaUD);
  };

  const handleAddPromoBreadToBill = (breadItem) => {
    const breadKey = getProductKey(breadItem.MaSP);

    setBillItems((prev) => {
      const addonedBreadKeys = new Set(
        prev.filter((item) => item.isAddon).map((item) => item.parentBillKey)
      );
      const plainBreadIndex = prev.findIndex(
        (item) =>
          isBreadLine(item) &&
          getProductKey(item.MaSP) === breadKey &&
          !addonedBreadKeys.has(item.billKey)
      );

      if (plainBreadIndex >= 0) {
        return prev.map((item, idx) =>
          idx === plainBreadIndex
            ? { ...item, quantity: (Number(item.quantity) || 0) + 1 }
            : item
        );
      }

      return [
        ...prev,
        {
          ...breadItem,
          quantity: 1,
          billKey: createBillKey("bread", breadKey),
        },
      ];
    });
    setDrinkSelections({});
    setTempDrinkSelections({});
  };

  const handleRemoveFromBill = (item) => {
    if (item.isAddon) {
      setBillItems((prev) =>
        prev.filter((bi) => bi.addonKey !== item.addonKey)
      );
      setDrinkSelections({});
      setTempDrinkSelections({});
      return;
    }

    if (isBreadLine(item)) {
      setBillItems((prev) =>
        prev.filter(
          (bi) =>
            bi.billKey !== item.billKey && bi.parentBillKey !== item.billKey
        )
      );
      setDrinkSelections({});
      setTempDrinkSelections({});
    }
  };

  const handleUpdateBillQuantity = (item, newQty) => {
    const num = Math.max(0, Number(newQty) || 0);
    if (num === 0) {
      handleRemoveFromBill(item);
    } else {
      if (item.isAddon) {
        setBillItems((prev) =>
          prev.map((bi) =>
            bi.addonKey === item.addonKey ? { ...bi, quantity: num } : bi
          )
        );
        setDrinkSelections({});
        setTempDrinkSelections({});
        return;
      }

      setBillItems((prev) =>
        prev.map((bi) =>
          bi.billKey === item.billKey
            ? { ...bi, quantity: num }
            : bi
        )
      );
      setDrinkSelections({});
      setTempDrinkSelections({});
    }
  };

  const handleOpenAddonModal = async (breadItem) => {
    if (!isBreadLine(breadItem)) return;

    let options = addonOptions;
    if (options.length === 0) {
      options = await fetchAddonOptions();
    }

    if (options.length === 0) {
      alert("Không có danh sách món ăn kèm để chọn");
      return;
    }

    setSelectedBreadForAddon(breadItem);
    setTempAddonSelections({});
    setShowAddonModal(true);
  };

  const handleTempAddonQuantityChange = (maSP, value) => {
    const addonKey = getProductKey(maSP);
    const nextQuantity = Math.max(0, Number(value) || 0);

    setTempAddonSelections((prev) => {
      const nextSelections = { ...prev };

      if (nextQuantity === 0) {
        delete nextSelections[addonKey];
      } else {
        nextSelections[addonKey] = nextQuantity;
      }

      return nextSelections;
    });
  };

  const handleTempAddonIncrement = (maSP) => {
    const addonKey = getProductKey(maSP);

    setTempAddonSelections((prev) => ({
      ...prev,
      [addonKey]: (Number(prev[addonKey]) || 0) + 1,
    }));
  };

  const handleTempAddonDecrement = (maSP) => {
    const addonKey = getProductKey(maSP);
    const currentQuantity = Number(tempAddonSelections[addonKey]) || 0;
    handleTempAddonQuantityChange(addonKey, currentQuantity - 1);
  };

  const handleConfirmAddonSelection = () => {
    if (!selectedBreadForAddon) return;

    const selectedAddons = Object.entries(tempAddonSelections)
      .map(([maSP, quantity]) => {
        const qty = Number(quantity) || 0;
        const addonItem = addonOptions.find(
          (addon) => getProductKey(addon.MaSP) === maSP
        );

        if (!addonItem || qty <= 0) return null;

        return {
          ...addonItem,
          quantity: qty,
          isAddon: true,
          addonKey: createBillKey("addon", addonItem.MaSP),
        };
      })
      .filter(Boolean);

    if (selectedAddons.length === 0) {
      alert("Vui lòng chọn món ăn kèm");
      return;
    }

    setBillItems((prev) => {
      const breadIndex = prev.findIndex(
        (item) => item.billKey === selectedBreadForAddon.billKey
      );

      if (breadIndex < 0) return prev;

      const breadItem = prev[breadIndex];
      const breadQuantity = Number(breadItem.quantity) || 0;

      if (breadQuantity > 1) {
        const addonedBreadKey = createBillKey("bread", breadItem.MaSP);
        const addonedBread = {
          ...breadItem,
          quantity: 1,
          billKey: addonedBreadKey,
        };
        const remainingBread = {
          ...breadItem,
          quantity: breadQuantity - 1,
        };
        const addonRows = selectedAddons.map((addon) => ({
          ...addon,
          parentBillKey: addonedBreadKey,
        }));
        const nextItems = [...prev];

        nextItems.splice(
          breadIndex,
          1,
          addonedBread,
          ...addonRows,
          remainingBread
        );
        return nextItems;
      }

      const addonRows = selectedAddons.map((addon) => ({
        ...addon,
        parentBillKey: breadItem.billKey,
      }));
      const lastLinkedAddonIndex = prev.reduce(
        (lastIndex, item, idx) =>
          item.parentBillKey === breadItem.billKey ? idx : lastIndex,
        -1
      );
      const insertIndex =
        lastLinkedAddonIndex >= 0 ? lastLinkedAddonIndex + 1 : breadIndex + 1;
      const nextItems = [...prev];

      nextItems.splice(insertIndex, 0, ...addonRows);
      return nextItems;
    });

    setShowAddonModal(false);
    setSelectedBreadForAddon(null);
    setTempAddonSelections({});
    setDrinkSelections({});
    setTempDrinkSelections({});
  };

  const totalBreadQuantity = useMemo(() => {
    return billItems.reduce(
      (sum, item) =>
        isBreadLine(item) ? sum + (Number(item.quantity) || 0) : sum,
      0
    );
  }, [billItems]);

  const eligibleDrinkQuantity = useMemo(() => {
    const addonTotalsByBread = billItems.reduce((result, item) => {
      if (!item.isAddon || !item.parentBillKey) return result;

      const addonPrice = Number(item.Gia || item.DonGia) || 0;
      const addonTotal = addonPrice * (Number(item.quantity) || 0);
      result[item.parentBillKey] =
        (result[item.parentBillKey] || 0) + addonTotal;
      return result;
    }, {});

    return billItems.reduce((sum, item) => {
      if (!isBreadLine(item)) return sum;

      const breadPrice = Number(item.Gia || item.DonGia) || 0;
      const linkedAddonTotal = addonTotalsByBread[item.billKey] || 0;
      const eligibleUnitAmount = breadPrice + linkedAddonTotal;

      return eligibleUnitAmount >= MIN_DRINK_ELIGIBLE_AMOUNT
        ? sum + (Number(item.quantity) || 0)
        : sum;
    }, 0);
  }, [billItems]);

  const selectedDrinkQuantity = useMemo(
    () => sumDrinkSelections(drinkSelections),
    [drinkSelections]
  );

  const tempDrinkQuantity = useMemo(
    () => sumDrinkSelections(tempDrinkSelections),
    [tempDrinkSelections]
  );

  const tempAddonQuantity = useMemo(
    () => sumDrinkSelections(tempAddonSelections),
    [tempAddonSelections]
  );

  const selectedDrinkItems = useMemo(() => {
    return Object.entries(drinkSelections)
      .map(([maSP, quantity]) => {
        const qty = Number(quantity) || 0;
        const drinkItem = drinkOptions.find(
          (drink) => getProductKey(drink.MaSP) === maSP
        );

        if (!drinkItem || qty <= 0) return null;

        return {
          ...drinkItem,
          quantity: qty,
          isPromo: true,
          promoKey: `promo_${maSP}`,
        };
      })
      .filter(Boolean);
  }, [drinkOptions, drinkSelections]);

  const drinkOptionRows = useMemo(() => {
    const rows = [];

    for (let index = 0; index < drinkOptions.length; index += 4) {
      rows.push(drinkOptions.slice(index, index + 4));
    }

    return rows;
  }, [drinkOptions]);

  const selectedBreadQuantities = useMemo(() => {
    return billItems.reduce((result, item) => {
      if (!isBreadLine(item)) return result;
      const itemKey = getProductKey(item.MaSP);
      result[itemKey] = (result[itemKey] || 0) + (Number(item.quantity) || 0);
      return result;
    }, {});
  }, [billItems]);

  const displayBillItems = useMemo(
    () => [...billItems, ...selectedDrinkItems],
    [billItems, selectedDrinkItems]
  );

  const remainingDrinkQuantity = Math.max(
    0,
    eligibleDrinkQuantity - selectedDrinkQuantity
  );

  const isDrinkSelectionComplete =
    selectedDrinkQuantity === eligibleDrinkQuantity;
  const hasEligibleDrinks = eligibleDrinkQuantity > 0;
  const hasBillItems = billItems.length > 0;

  const handleOpenDrinkModal = () => {
    if (billItems.length === 0) {
      alert("Vui lòng thêm sản phẩm vào hóa đơn");
      return;
    }

    if (!hasEligibleDrinks) {
      alert("Chưa có sản phẩm đủ điều kiện nhận nước tặng");
      return;
    }

    if (drinkOptions.length === 0) {
      alert("Không có danh sách nước tặng để chọn");
      return;
    }

    setTempDrinkSelections(drinkSelections);
    setShowDrinkModal(true);
  };

  const handleTempDrinkQuantityChange = (maSP, value) => {
    const drinkKey = getProductKey(maSP);

    setTempDrinkSelections((prev) => {
      const otherQuantity = Object.entries(prev).reduce(
        (sum, [key, quantity]) =>
          key === drinkKey ? sum : sum + (Number(quantity) || 0),
        0
      );
      const maxQuantity = Math.max(0, eligibleDrinkQuantity - otherQuantity);
      const nextQuantity = Math.min(
        Math.max(0, Number(value) || 0),
        maxQuantity
      );
      const nextSelections = { ...prev };

      if (nextQuantity === 0) {
        delete nextSelections[drinkKey];
      } else {
        nextSelections[drinkKey] = nextQuantity;
      }

      return nextSelections;
    });
  };

  const handleTempDrinkIncrement = (maSP) => {
    const drinkKey = getProductKey(maSP);

    setTempDrinkSelections((prev) => {
      if (sumDrinkSelections(prev) >= eligibleDrinkQuantity) return prev;

      return {
        ...prev,
        [drinkKey]: (Number(prev[drinkKey]) || 0) + 1,
      };
    });
  };

  const handleTempDrinkDecrement = (maSP) => {
    const drinkKey = getProductKey(maSP);
    const currentQuantity = Number(tempDrinkSelections[drinkKey]) || 0;
    handleTempDrinkQuantityChange(drinkKey, currentQuantity - 1);
  };

  const handleConfirmDrinkSelection = () => {
    if (tempDrinkQuantity !== eligibleDrinkQuantity) {
      alert(`Vui lòng chọn đủ ${eligibleDrinkQuantity} nước tặng`);
      return;
    }

    const normalizedSelections = Object.entries(tempDrinkSelections).reduce(
      (result, [maSP, quantity]) => {
        const qty = Number(quantity) || 0;
        if (qty > 0) result[maSP] = qty;
        return result;
      },
      {}
    );

    setDrinkSelections(normalizedSelections);
    setShowDrinkModal(false);
  };

  const totalAmount = useMemo(() => {
    return billItems.reduce((sum, item) => {
      if (item.isPromo) return sum; // Free items don`t count
      const price = item.Gia || item.DonGia || 0;
      return sum + price * item.quantity;
    }, 0);
  }, [billItems]);
  const cashQuickAmounts = useMemo(() => {
    if (totalAmount <= 0) return [];

    const roundTo = (amount, step) => Math.ceil(amount / step) * step;

    return Array.from(
      new Set([
        totalAmount,
        roundTo(totalAmount, 50000),
        roundTo(totalAmount, 100000),
        roundTo(totalAmount, 200000),
      ])
    ).filter((amount) => amount > 0);
  }, [totalAmount]);
  const numericCashGiven = Number(cashGiven) || 0;
  const cashChangeAmount = Math.max(0, numericCashGiven - totalAmount);
  const isCashPaymentInvalid =
    paymentMethod === "cash" && (!cashGiven || numericCashGiven < totalAmount);
  const transferQRCodeSrc = `${process.env.REACT_APP_SEPAY_QR_BASE_URL}/img?acc=${
    localStorage.getItem("stk") || ""
  }&template=qronly&bank=${
    localStorage.getItem("bank_name") || ""
  }&amount=${totalAmount}&des=Thanh toan hoa don tai ${
    localStorage.getItem("TenCuaHang") || ""
  }`;
  const getDrinkGridStyle = (optionCount) =>
    optionCount > 0 && optionCount <= 2
      ? {
          ...styles.drinkGrid,
          maxWidth: optionCount === 1 ? "170px" : "350px",
          margin: "0 auto",
        }
      : styles.drinkGrid;

  const handleTransferQRCodeError = (event) => {
    if (event.currentTarget.dataset.fallbackQr === "true") return;

    event.currentTarget.dataset.fallbackQr = "true";
    event.currentTarget.src = "/qr.png";
  };

  const handleClosePaymentModal = () => {
    setShowPaymentModal(false);
    setShowTransferQRZoom(false);
  };

  const handleCreateBill = async () => {
    if (billItems.length === 0) {
      alert("Vui lòng thêm sản phẩm vào hóa đơn");
      return;
    }

    // Transform billItems to orderItems format - including both regular and promo items
    const orderItems = [...billItems, ...selectedDrinkItems].map((item) => {
      const price = item.Gia || item.DonGia || 0;
      const kmaiPT = item.isPromo ? 100 : 0;
      const thanhTien = item.isPromo ? 0 : price * item.quantity;
      const category =
        item.category ||
        item.categoryId ||
        item.MaNLSP ||
        item.MaLSP ||
        item.MaNhom ||
        "Unknown";

      return {
        id: item.MaSP,
        name: item.Ten_SP,
        quantity: item.quantity,
        price: price, // Keep original price even for promo items
        unit: item.DVT_SP || item.DVT || "",
        note: item.note || (item.isPromo ? "Tặng kèm khuyến mãi" : ""),
        category,
        vat: item.VAT_SP || item.vat || 0,
        KmaiPT: kmaiPT, // 100 for promo items, 0 for regular items
        ThanhTien: thanhTien, // 0 for promo items
        MXTY: 2,
      };
    });

    const subtotal = totalAmount;
    const total = totalAmount;

    await processCheckout({
      orderItems,
      subtotal,
      total,
      discountPercent: 0,
      voucherDiscount: 0,
      voucherCode: "",
      usePointValue: 0,
      membershipDiscountAmount: 0,
      paymentMethod,
      cashGiven,
      selectedTable: null,
      billNote,
      isPrintEnabled: true,
      isDisablePrintAPI,
      onSuccess: () => {
        // Clear bill after successful checkout
        setBillItems([]);
        setDrinkSelections({});
        setTempDrinkSelections({});
        setShowAddonModal(false);
        setSelectedBreadForAddon(null);
        setTempAddonSelections({});
        setPaymentMethod("cash");
        setCashGiven("");
        setBillNote("");
        setShowIncompleteDrinkWarning(false);
        handleClosePaymentModal();
      },
      onError: (err) => {
        console.error("Checkout failed:", err);
      },
    });
  };

  const openPaymentModal = () => {
    // Set default cash given to total amount
    setCashGiven(totalAmount.toString());
    setShowPaymentModal(true);
  };

  const handleOpenPaymentModal = () => {
    if (billItems.length === 0) {
      alert("Vui lòng thêm sản phẩm vào hóa đơn");
      return;
    }

    if (!isDrinkSelectionComplete) {
      setShowIncompleteDrinkWarning(true);
      return;
    }

    openPaymentModal();
  };

  const handleContinueWithoutFullDrinkSelection = () => {
    setShowIncompleteDrinkWarning(false);
    openPaymentModal();
  };

  const handleChooseDrinkFromWarning = () => {
    setShowIncompleteDrinkWarning(false);
    handleOpenDrinkModal();
  };

  const handleClearBill = () => {
    setBillItems([]);
    setDrinkSelections({});
    setTempDrinkSelections({});
    setShowAddonModal(false);
    setSelectedBreadForAddon(null);
    setTempAddonSelections({});
    setPaymentMethod("cash");
    setCashGiven("");
    setBillNote("");
    setShowIncompleteDrinkWarning(false);
    setShowTransferQRZoom(false);
  };

  const handleToggleDisablePrintAPI = () => {
    setIsDisablePrintAPI((prev) => {
      const nextValue = !prev;
      localStorage.setItem(
        DISABLE_PRINT_API_STORAGE_KEY,
        nextValue ? "true" : "false"
      );
      return nextValue;
    });
  };

  useEffect(() => {
    // Add Bootstrap CSS if not already present
    if (!document.getElementById("bootstrap-css")) {
      const link = document.createElement("link");
      link.id = "bootstrap-css";
      link.rel = "stylesheet";
      link.href =
        "https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css";
      document.head.appendChild(link);
    }

    if (!document.getElementById(PAGE_STYLE_ID)) {
      const style = document.createElement("style");
      style.id = PAGE_STYLE_ID;
      style.textContent = `
        .uu-dai-ct-page {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }

        .uu-dai-ct-page input,
        .uu-dai-ct-page textarea,
        .uu-dai-ct-page select {
          -webkit-user-select: text;
          -moz-user-select: text;
          -ms-user-select: text;
          user-select: text;
        }

        @media (max-width: 520px) {
          .uu-dai-ct-drink-grid {
            grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)) !important;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div className="uu-dai-ct-page" style={styles.container}>
      {/* Header */}

      {loading && <div style={styles.loading}>Đang tải...</div>}
      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.mainContent}>
        {/* Left Panel - Products & Promotions */}
        <div style={styles.leftPanel}>
          {/* Promotion Selection */}
          <div style={styles.promotionSection}>
            <div className="uu-dai-ct-top-toolbar" style={styles.topToolbar}>
              <button
                type="button"
                className="btn"
                style={styles.backButton}
                onClick={() => {
                  window.location.href = "/order/dongtien";
                }}
              >
                <span aria-hidden="true">←</span>
                Trở về
              </button>

              <div style={styles.searchArea}>
                <div style={styles.searchControlRow}>
                  <div style={styles.searchInputWrapper}>
                    <span style={styles.searchIcon} aria-hidden="true">
                      🔎
                    </span>
                    <input
                      ref={searchInputRef}
                      type="text"
                      inputMode="none"
                      value={productSearch}
                      placeholder="Tìm theo tên hoặc giá sản phẩm..."
                      style={styles.searchInput}
                      onChange={(event) => setProductSearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") {
                          setShowSearchKeyboard(false);
                        }
                      }}
                      aria-label="Tìm sản phẩm theo tên hoặc giá"
                    />
                    {productSearch && (
                      <button
                        type="button"
                        className="btn"
                        style={styles.clearSearchButton}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                          setProductSearch("");
                          searchInputRef.current?.focus();
                        }}
                        aria-label="Xóa nội dung tìm kiếm"
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <button
                    type="button"
                    className="btn"
                    style={{
                      ...styles.searchKeyboardToggle,
                      ...(showSearchKeyboard
                        ? styles.searchKeyboardToggleActive
                        : {}),
                    }}
                    onClick={handleToggleSearchKeyboard}
                    aria-label={
                      showSearchKeyboard
                        ? "Đóng bàn phím ảo"
                        : "Mở bàn phím ảo"
                    }
                    aria-pressed={showSearchKeyboard}
                    title={
                      showSearchKeyboard
                        ? "Đóng bàn phím ảo"
                        : "Mở bàn phím ảo"
                    }
                  >
                    ⌨️
                  </button>
                </div>

                {showSearchKeyboard && (
                  <div
                    style={styles.searchKeyboard}
                    onMouseDown={(event) => event.preventDefault()}
                  >
                    {SEARCH_KEYBOARD_ROWS.map((row, rowIndex) => (
                      <div
                        key={`search-keyboard-row-${rowIndex}`}
                        style={styles.searchKeyboardRow}
                      >
                        {row.map((key) => (
                          <button
                            key={key}
                            type="button"
                            className="btn"
                            style={styles.searchKeyboardKey}
                            onClick={() => handleSearchKeyboardKey(key)}
                          >
                            {key}
                          </button>
                        ))}
                      </div>
                    ))}
                    <div style={styles.searchKeyboardActionRow}>
                      <button
                        type="button"
                        className="btn"
                        style={styles.searchKeyboardActionKey}
                        onClick={() => handleSearchKeyboardKey("clear")}
                      >
                        Xóa
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={{
                          ...styles.searchKeyboardActionKey,
                          flex: "2 1 180px",
                        }}
                        onClick={() => handleSearchKeyboardKey("space")}
                      >
                        Dấu cách
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={styles.searchKeyboardActionKey}
                        onClick={() => handleSearchKeyboardKey("backspace")}
                        aria-label="Xóa ký tự cuối"
                      >
                        ⌫
                      </button>
                      <button
                        type="button"
                        className="btn"
                        style={styles.searchKeyboardCloseKey}
                        onClick={() => setShowSearchKeyboard(false)}
                      >
                        Đóng
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <select
              className="form-select form-select-lg"
              value={selectedPromotion?.MaUD || ""}
              disabled
            >
              <option value="">-- Chọn ưu đãi --</option>
              {buyBreadGetDrink.map((promo) => (
                <option key={promo.MaUD} value={promo.MaUD}>
                  {promo.TenUD || `Ưu đãi ${promo.MaUD}`}
                </option>
              ))}
            </select>
          </div>

          {/* Product Filter Buttons */}
          {selectedPromotion && (
            <div style={styles.filterSection}>
              <button
                className={`btn ${
                  productFilter === "banhmi"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                style={styles.filterButton}
                onClick={() => setProductFilter("banhmi")}
              >
                🥖 Bánh mì
              </button>
              <button
                className={`btn ${
                  productFilter === "burger"
                    ? "btn-primary"
                    : "btn-outline-primary"
                }`}
                style={styles.filterButton}
                onClick={() => setProductFilter("burger")}
              >
                🍔 Burger
              </button>
            </div>
          )}

          {/* Promotion Products */}
          {selectedPromotion && (
            <div style={styles.promoProducts}>
              <h4 style={styles.subTitle}>
                🎁 Sản phẩm khuyến mãi (Mua bánh tặng nước)
              </h4>
              <div ref={productGridRef} style={styles.productGrid}>
                {filteredPromoProducts.length === 0 && (
                  <div style={styles.emptyProductSearch}>
                    Không tìm thấy sản phẩm phù hợp
                  </div>
                )}
                {filteredPromoProducts.map((sp, idx) => {
                  const selectedQuantity =
                    selectedBreadQuantities[getProductKey(sp.MaSP)] || 0;
                  const columnIndex = idx % productGridColumnCount;

                  return (
                    <div
                      key={sp.MaSP}
                      style={{
                        ...styles.promoCard,
                        ...(columnIndex % 2 === 0
                          ? styles.promoCardColumnOdd
                          : styles.promoCardColumnEven),
                        ...(selectedQuantity > 0
                          ? styles.promoCardSelected
                          : {}),
                      }}
                      onClick={() => handleAddPromoBreadToBill(sp)}
                    >
                      <div style={styles.promoCardHeader}>
                        <strong style={styles.promoCardName}>
                          {sp.Ten_SP}
                        </strong>
                      </div>
                      <div style={styles.promoCardPrice}>
                        {(sp.Gia || sp.DonGia || 0).toLocaleString()}đ
                      </div>
                      <div
                        style={{
                          ...styles.selectedQuantityBadge,
                          ...(selectedQuantity > 0
                            ? {}
                            : styles.selectedQuantityBadgeHidden),
                        }}
                      >
                        Đã chọn:{" "}
                        <span style={{ color: "#ffdfb5" }}>
                          {selectedQuantity}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Panel - Bill */}
        <div style={styles.rightPanel}>
          <h3 style={styles.sectionTitle}>🧾 Hóa đơn</h3>

          <div style={styles.billContent}>
            {billItems.length === 0 ? (
              <div style={styles.emptyBill}>Chưa có sản phẩm</div>
            ) : (
              <>
                {displayBillItems.map((item, idx) => {
                  const price = item.Gia || item.DonGia || 0;
                  const lineTotal = price * item.quantity;
                  const hasLinkedAddons =
                    isBreadLine(item) &&
                    billItems.some(
                      (billItem) => billItem.parentBillKey === item.billKey
                    );
                  const canEditQuantity =
                    item.isAddon || (isBreadLine(item) && !hasLinkedAddons);

                  return (
                    <div
                      key={
                        item.addonKey ||
                        item.promoKey ||
                        item.billKey ||
                        `${item.MaSP}-${idx}`
                      }
                      style={
                        item.isPromo
                          ? styles.billItemPromo
                          : item.isAddon
                          ? styles.billItemAddon
                          : styles.billItem
                      }
                    >
                      <div style={styles.billItemInfo}>
                        <div style={styles.billItemName}>
                          {item.isPromo && "🎁 "}
                          {item.isAddon && "+ "}
                          {item.Ten_SP}
                        </div>
                        <div style={styles.billItemPrice}>
                          {item.isPromo
                            ? `Số lượng: ${item.quantity} - Miễn phí`
                            : `${price.toLocaleString()}đ x ${
                                item.quantity
                              } = ${lineTotal.toLocaleString()}đ`}
                        </div>
                      </div>
                      <div style={styles.billItemActions}>
                        {!item.isPromo && (
                          <>
                            {canEditQuantity && (
                              <div style={styles.quantityControlGroup}>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleUpdateBillQuantity(
                                      item,
                                      item.quantity - 1
                                    )
                                  }
                                  style={styles.btnMinus}
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={(e) =>
                                    handleUpdateBillQuantity(
                                      item,
                                      e.target.value
                                    )
                                  }
                                  style={styles.billQuantityInput}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleUpdateBillQuantity(
                                      item,
                                      item.quantity + 1
                                    )
                                  }
                                  style={styles.btnPlus}
                                >
                                  +
                                </button>
                              </div>
                            )}
                            <div style={styles.billItemCommandRow}>
                              {isBreadLine(item) && (
                                <button
                                  type="button"
                                  className="btn"
                                  style={styles.addonButton}
                                  onClick={() => handleOpenAddonModal(item)}
                                >
                                  + Ăn kèm
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => handleRemoveFromBill(item)}
                                className="btn"
                                style={styles.removeItemButton}
                                aria-label="Xóa món"
                              >
                                ×
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Total */}
          <div style={styles.totalSection}>
            <div style={styles.totalRow}>
              <span style={styles.totalLabel}>Tổng cộng</span>
              <strong style={styles.totalAmount}>
                {totalAmount.toLocaleString()}đ
              </strong>
            </div>
            {totalBreadQuantity > 0 && (
              <div style={styles.drinkSelectionStatus}>
                <div>
                  <div style={styles.drinkSelectionLabel}>Nước tặng</div>
                  <div
                    style={
                      isDrinkSelectionComplete
                        ? styles.drinkSelectionReady
                        : styles.drinkSelectionPending
                    }
                  >
                    {isDrinkSelectionComplete
                      ? hasEligibleDrinks
                        ? "Đã chọn đủ nước tặng"
                        : "Không có suất nước tặng"
                      : `Còn ${remainingDrinkQuantity} ly cần chọn`}
                  </div>
                </div>
                <span
                  style={
                    isDrinkSelectionComplete
                      ? styles.drinkSelectionBadgeReady
                      : styles.drinkSelectionBadgePending
                  }
                >
                  {selectedDrinkQuantity}/{eligibleDrinkQuantity}
                </span>
              </div>
            )}

            {/* Action Buttons */}
            <div style={styles.actionButtons}>
              <div style={styles.secondaryActionRow}>
                <button
                  type="button"
                  className="btn"
                  style={{
                    ...styles.billSecondaryButton,
                    ...(!hasBillItems ? styles.billButtonDisabled : {}),
                    border: "2px solid #c92c04",
                  }}
                  onClick={handleClearBill}
                  disabled={!hasBillItems}
                >
                  <span style={styles.billButtonIcon}>↺</span>
                  Xóa hết
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{
                    ...styles.billSecondaryButton,
                    ...(hasEligibleDrinks && isDrinkSelectionComplete
                      ? styles.billSecondaryButtonActive
                      : {}),
                    ...(!hasBillItems || !hasEligibleDrinks
                      ? styles.billButtonDisabled
                      : {}),
                    border: "2px solid #276b00",
                  }}
                  onClick={handleOpenDrinkModal}
                  disabled={!hasBillItems || !hasEligibleDrinks}
                >
                  <span style={styles.billButtonIcon}>🥤</span>
                  {!hasEligibleDrinks
                    ? "Không có nước"
                    : isDrinkSelectionComplete
                    ? "Đổi nước"
                    : "Chọn nước"}
                </button>
              </div>
              <button
                type="button"
                className="btn"
                style={{
                  ...styles.checkoutButton,
                  ...(isDrinkSelectionComplete
                    ? styles.checkoutButtonReady
                    : styles.checkoutButtonPending),
                  ...(!hasBillItems ? styles.checkoutButtonDisabled : {}),
                }}
                onClick={handleOpenPaymentModal}
                disabled={!hasBillItems}
              >
                <span style={styles.checkoutButtonMain}>Thanh toán</span>
                <span style={styles.checkoutButtonSub}>
                  {totalAmount.toLocaleString()}đ
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Incomplete Drink Selection Warning */}
      {showIncompleteDrinkWarning && (
        <div
          style={styles.modalOverlay}
          onClick={() => setShowIncompleteDrinkWarning(false)}
        >
          <div
            style={{ ...styles.modalContent, maxWidth: "460px" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeroHeader}>
              <div style={styles.modalTitleGroup}>
                <span style={styles.modalIconBox}>⚠️</span>
                <div>
                  <div style={styles.modalKicker}>Xác nhận thanh toán</div>
                  <h3 style={styles.modalTitle}>Chưa chọn đủ nước</h3>
                </div>
              </div>
              <button
                type="button"
                className="btn"
                style={styles.modalCloseButton}
                onClick={() => setShowIncompleteDrinkWarning(false)}
                aria-label="Đóng"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.warningModalMessage}>
                Bạn có muốn tiếp tục thanh toán mà không chọn nước ?
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                className="btn"
                style={styles.modalSecondaryButton}
                onClick={handleContinueWithoutFullDrinkSelection}
              >
                Tiếp tục
              </button>
              <button
                type="button"
                className="btn"
                style={styles.modalPrimaryButton}
                onClick={handleChooseDrinkFromWarning}
              >
                Chọn nước
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Drink Selection Modal */}
      {showDrinkModal && (
        <div
          style={styles.modalOverlay}
          onClick={() => setShowDrinkModal(false)}
        >
          <div
            style={{ ...styles.modalContent, ...styles.selectionModalContent }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeroHeader}>
              <div style={styles.modalTitleGroup}>
                <span style={styles.modalIconBox}>🥤</span>
                <div>
                  <div style={styles.modalKicker}>Ưu đãi mua bánh tặng nước</div>
                  <h3 style={styles.modalTitle}>Chọn nước tặng kèm</h3>
                </div>
              </div>
              <button
                type="button"
                className="btn"
                style={styles.modalCloseButton}
                onClick={() => setShowDrinkModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.selectionSummaryGrid}>
                <div style={styles.selectionSummaryCard}>
                  <span style={styles.selectionSummaryLabel}>
                    Suất được tặng
                  </span>
                  <strong style={styles.selectionSummaryValue}>
                    {eligibleDrinkQuantity}
                  </strong>
                </div>
                <div style={styles.selectionSummaryCard}>
                  <span style={styles.selectionSummaryLabel}>Nước đã chọn</span>
                  <strong style={styles.selectionSummaryValue}>
                    {tempDrinkQuantity}
                  </strong>
                </div>
                <div
                  style={{
                    ...styles.selectionSummaryCard,
                    ...(tempDrinkQuantity === eligibleDrinkQuantity
                      ? styles.selectionSummaryCardReady
                      : {}),
                  }}
                >
                  <span style={styles.selectionSummaryLabel}>Còn lại</span>
                  <strong style={styles.selectionSummaryValue}>
                    {Math.max(0, eligibleDrinkQuantity - tempDrinkQuantity)}
                  </strong>
                </div>
              </div>

              {drinkOptions.length === 0 ? (
                <div style={styles.emptyBill}>Không có danh sách nước tặng</div>
              ) : (
                <div style={styles.drinkGridStack}>
                  {drinkOptionRows.map((row, rowIndex) => (
                    <div
                      key={`drink-row-${rowIndex}`}
                      className="uu-dai-ct-drink-grid"
                      style={getDrinkGridStyle(row.length)}
                    >
                      {row.map((drink) => {
                        const drinkKey = getProductKey(drink.MaSP);
                        const selectedQuantity =
                          Number(tempDrinkSelections[drinkKey]) || 0;
                        const isDisabledDrink =
                          selectedQuantity === 0 &&
                          tempDrinkQuantity >= eligibleDrinkQuantity;

                        return (
                          <div
                            key={drink.MaSP}
                            style={{
                              ...styles.drinkOption,
                              ...(selectedQuantity > 0
                                ? styles.drinkOptionSelected
                                : {}),
                              ...(isDisabledDrink
                                ? styles.drinkOptionDisabled
                                : {}),
                            }}
                            onClick={() => {
                              if (!isDisabledDrink) {
                                handleTempDrinkIncrement(drink.MaSP);
                              }
                            }}
                          >
                            <div style={styles.drinkOptionTopRow}>
                              <span style={styles.drinkCode}>#{drink.MaSP}</span>
                              {selectedQuantity > 0 && (
                                <span style={styles.drinkSelectedBadge}>
                                  x{selectedQuantity}
                                </span>
                              )}
                            </div>
                            <div style={styles.drinkName}>{drink.Ten_SP}</div>
                            <div style={styles.drinkQuantityControls}>
                              <button
                                type="button"
                                style={styles.btnMinus}
                                disabled={selectedQuantity === 0}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTempDrinkDecrement(drink.MaSP);
                                }}
                              >
                                -
                              </button>
                              <input
                                type="number"
                                min="0"
                                value={selectedQuantity}
                                style={styles.drinkQuantityInput}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) =>
                                  handleTempDrinkQuantityChange(
                                    drink.MaSP,
                                    e.target.value
                                  )
                                }
                              />
                              <button
                                type="button"
                                style={styles.btnPlus}
                                disabled={
                                  tempDrinkQuantity >= eligibleDrinkQuantity
                                }
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTempDrinkIncrement(drink.MaSP);
                                }}
                              >
                                +
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                className="btn"
                style={styles.modalSecondaryButton}
                onClick={() => setShowDrinkModal(false)}
              >
                Hủy
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  ...styles.modalPrimaryButton,
                  ...(drinkOptions.length === 0 ||
                  tempDrinkQuantity !== eligibleDrinkQuantity
                    ? styles.modalPrimaryButtonDisabled
                    : {}),
                }}
                onClick={handleConfirmDrinkSelection}
                disabled={
                  drinkOptions.length === 0 ||
                  tempDrinkQuantity !== eligibleDrinkQuantity
                }
              >
                Xác nhận {tempDrinkQuantity}/{eligibleDrinkQuantity}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Addon Selection Modal */}
      {showAddonModal && (
        <div
          style={styles.modalOverlay}
          onClick={() => setShowAddonModal(false)}
        >
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Chọn món ăn kèm</h3>
              <button
                className="btn-close"
                onClick={() => setShowAddonModal(false)}
                aria-label="Close"
              ></button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.drinkSummary}>
                <span>{selectedBreadForAddon?.Ten_SP}</span>
                <span>Đã chọn: {tempAddonQuantity}</span>
              </div>

              {addonOptions.length === 0 ? (
                <div style={styles.emptyBill}>Không có danh sách món ăn kèm</div>
              ) : (
                <div style={styles.drinkGrid}>
                  {addonOptions.map((addon) => {
                    const addonKey = getProductKey(addon.MaSP);
                    const selectedQuantity =
                      Number(tempAddonSelections[addonKey]) || 0;

                    return (
                      <div
                        key={addon.MaSP}
                        style={{
                          ...styles.drinkOption,
                          ...(selectedQuantity > 0
                            ? styles.drinkOptionSelected
                            : {}),
                        }}
                        onClick={() => handleTempAddonIncrement(addon.MaSP)}
                      >
                        <div style={styles.drinkName}>{addon.Ten_SP}</div>
                        <div style={styles.drinkCode}>
                          {(addon.Gia || addon.DonGia || 0).toLocaleString()}đ
                        </div>
                        <div style={styles.drinkQuantityControls}>
                          <button
                            type="button"
                            style={styles.btnMinus}
                            disabled={selectedQuantity === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTempAddonDecrement(addon.MaSP);
                            }}
                          >
                            -
                          </button>
                          <input
                            type="number"
                            min="0"
                            value={selectedQuantity}
                            style={styles.drinkQuantityInput}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) =>
                              handleTempAddonQuantityChange(
                                addon.MaSP,
                                e.target.value
                              )
                            }
                          />
                          <button
                            type="button"
                            style={styles.btnPlus}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTempAddonIncrement(addon.MaSP);
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div style={styles.modalFooter}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddonModal(false)}
              >
                Hủy
              </button>
              <button
                className="btn btn-success"
                onClick={handleConfirmAddonSelection}
                disabled={addonOptions.length === 0 || tempAddonQuantity === 0}
              >
                Xác nhận
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div
          style={styles.modalOverlay}
          onClick={handleClosePaymentModal}
        >
          <div
            style={styles.paymentModalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={styles.modalHeroHeader}>
              <div style={styles.modalTitleGroup}>
                <span style={styles.modalIconBox}>💳</span>
                <div>
                  <div style={styles.modalKicker}>Hoàn tất hóa đơn</div>
                  <h3 style={styles.modalTitle}>Thanh toán</h3>
                </div>
              </div>
              <button
                type="button"
                className="btn"
                style={styles.modalCloseButton}
                onClick={handleClosePaymentModal}
                aria-label="Close"
                disabled={isProcessing}
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.paymentTotalDisplay}>
                <div>
                  <span style={styles.paymentTotalLabel}>Tổng thanh toán</span>
                  <strong style={styles.paymentTotalAmount}>
                    {totalAmount.toLocaleString()}đ
                  </strong>
                </div>
                <span style={styles.paymentTotalBadge}>
                  Nước {selectedDrinkQuantity}/{eligibleDrinkQuantity}
                </span>
              </div>

              <div style={styles.paymentSection}>
                <div style={styles.paymentSectionTitle}>
                  Phương thức thanh toán
                </div>
                <div style={styles.paymentMethodGrid}>
                  {PAYMENT_METHOD_OPTIONS.map((method) => {
                    const isSelected = paymentMethod === method.value;

                    return (
                      <button
                        type="button"
                        key={method.value}
                        className="btn"
                        style={{
                          ...styles.paymentMethodButton,
                          ...(isSelected ? styles.paymentMethodButtonActive : {}),
                        }}
                        onClick={() => setPaymentMethod(method.value)}
                        disabled={isProcessing}
                      >
                        <span style={styles.paymentMethodIcon}>
                          {method.icon}
                        </span>
                        <span style={styles.paymentMethodLabel}>
                          {method.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {paymentMethod === "cash" && (
                <div style={styles.paymentSection}>
                  <div style={styles.paymentSectionHeader}>
                    <span style={styles.paymentSectionTitle}>
                      Tiền khách đưa
                    </span>
                    <span style={styles.paymentHint}>
                      Cần {totalAmount.toLocaleString()}đ
                    </span>
                  </div>
                  <input
                    type="number"
                    value={cashGiven}
                    onChange={(e) => setCashGiven(e.target.value)}
                    placeholder="Nhập số tiền khách đưa"
                    style={styles.paymentInput}
                  />
                  {cashQuickAmounts.length > 0 && (
                    <div style={styles.cashQuickRow}>
                      {cashQuickAmounts.map((amount) => (
                        <button
                          type="button"
                          key={amount}
                          className="btn"
                          style={{
                            ...styles.cashQuickButton,
                            ...(numericCashGiven === amount
                              ? styles.cashQuickButtonActive
                              : {}),
                          }}
                          onClick={() => setCashGiven(String(amount))}
                        >
                          {amount.toLocaleString()}đ
                        </button>
                      ))}
                    </div>
                  )}
                  {cashGiven && !isCashPaymentInvalid && (
                    <div style={styles.paymentStatusSuccess}>
                      Tiền thừa trả khách:{" "}
                      <strong>{cashChangeAmount.toLocaleString()}đ</strong>
                    </div>
                  )}
                  {cashGiven && isCashPaymentInvalid && (
                    <div style={styles.paymentStatusWarning}>
                      Còn thiếu{" "}
                      <strong>
                        {(totalAmount - numericCashGiven).toLocaleString()}đ
                      </strong>
                    </div>
                  )}
                </div>
              )}

              {paymentMethod === "transfer" && (
                <div style={styles.paymentSection}>
                  <div style={styles.paymentSectionHeader}>
                    <span style={styles.paymentSectionTitle}>
                      Mã QR chuyển khoản
                    </span>
                    <span style={styles.paymentHint}>
                      {totalAmount.toLocaleString()}đ
                    </span>
                  </div>
                  <button
                    type="button"
                    className="btn"
                    style={styles.transferQRButton}
                    onClick={() => setShowTransferQRZoom(true)}
                    aria-label="Phóng to mã QR chuyển khoản"
                    title="Phóng to mã QR"
                  >
                    <img
                      src={transferQRCodeSrc}
                      alt="QR Code Chuyển khoản"
                      style={styles.transferQRCode}
                      onError={handleTransferQRCodeError}
                    />
                  </button>
                </div>
              )}

              <div style={styles.paymentSection}>
                <div style={styles.paymentSectionHeader}>
                  <span style={styles.paymentSectionTitle}>
                    Ghi chú đơn hàng
                  </span>
                  <label style={styles.paymentToggleLabel}>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={isDisablePrintAPI}
                      onChange={handleToggleDisablePrintAPI}
                    />
                    Tắt API in
                  </label>
                </div>
                <textarea
                  value={billNote}
                  onChange={(e) => setBillNote(e.target.value)}
                  placeholder="Nhập ghi chú cho đơn hàng (nếu có)"
                  rows="3"
                  style={styles.paymentTextarea}
                />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                type="button"
                className="btn"
                style={styles.modalSecondaryButton}
                onClick={handleClosePaymentModal}
                disabled={isProcessing}
              >
                Hủy
              </button>
              <button
                type="button"
                className="btn"
                style={{
                  ...styles.modalPrimaryButton,
                  ...(isProcessing || isCashPaymentInvalid
                    ? styles.modalPrimaryButtonDisabled
                    : {}),
                }}
                onClick={handleCreateBill}
                disabled={isProcessing || isCashPaymentInvalid}
              >
                {isProcessing ? "Đang xử lý..." : "Xác nhận thanh toán"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTransferQRZoom && (
        <div
          style={styles.qrZoomOverlay}
          onClick={() => setShowTransferQRZoom(false)}
        >
          <div style={styles.qrZoomContent} onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="btn"
              style={styles.qrZoomCloseButton}
              onClick={() => setShowTransferQRZoom(false)}
              aria-label="Close"
            >
              ×
            </button>
            <img
              src={transferQRCodeSrc}
              alt="QR Code Chuyển khoản"
              style={styles.transferQRCodeLarge}
              onError={handleTransferQRCodeError}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    height: "100vh",
    display: "flex",
    flexDirection: "column",
    fontFamily: "`Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    backgroundColor: "#f5f5f5",
  },
  header: {
    backgroundColor: "#2c3e50",
    color: "white",
    padding: "16px 24px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  title: {
    margin: 0,
    fontSize: "24px",
  },
  storeInfo: {
    fontSize: "14px",
    opacity: 0.9,
  },
  mainContent: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  leftPanel: {
    flex: "1 1 60%",
    padding: "20px",
    overflowY: "auto",
    backgroundColor: "#fff",
    borderRight: "2px solid #e0e0e0",
  },
  rightPanel: {
    flex: "0 0 400px",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#fafafa",
    boxShadow: "-2px 0 8px rgba(0,0,0,0.05)",
  },
  sectionTitle: {
    margin: "0 0 16px 0",
    fontSize: "20px",
    color: "#2c3e50",
    padding: "12px 20px",
    backgroundColor: "#ecf0f1",
    cursor: "pointer",
  },
  topToolbar: {
    display: "flex",
    alignItems: "flex-start",
    gap: "10px",
    marginBottom: "12px",
  },
  backButton: {
    flex: "0 0 auto",
    minHeight: "44px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    padding: "8px 12px",
    border: "1px solid #cbd5e1",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    color: "#334155",
    fontSize: "14px",
    fontWeight: "800",
    whiteSpace: "nowrap",
  },
  searchArea: {
    flex: "1 1 auto",
    minWidth: 0,
  },
  searchControlRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  searchInputWrapper: {
    position: "relative",
    flex: "1 1 auto",
    minWidth: 0,
  },
  searchIcon: {
    position: "absolute",
    top: "50%",
    left: "12px",
    transform: "translateY(-50%)",
    fontSize: "16px",
    lineHeight: "1",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    height: "44px",
    padding: "9px 42px 9px 40px",
    border: "2px solid #cbd5e1",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#26384d",
    fontSize: "16px",
    fontWeight: "600",
    outline: "none",
    boxSizing: "border-box",
  },
  clearSearchButton: {
    position: "absolute",
    top: "50%",
    right: "7px",
    transform: "translateY(-50%)",
    width: "30px",
    height: "30px",
    padding: 0,
    border: "none",
    borderRadius: "6px",
    backgroundColor: "#eef2f7",
    color: "#64748b",
    fontSize: "20px",
    fontWeight: "700",
    lineHeight: "1",
  },
  searchKeyboardToggle: {
    flex: "0 0 46px",
    width: "46px",
    height: "44px",
    padding: 0,
    border: "1px solid #94a3b8",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    color: "#334155",
    fontSize: "20px",
    lineHeight: "1",
  },
  searchKeyboardToggleActive: {
    borderColor: "#2563eb",
    backgroundColor: "#dbeafe",
    color: "#1d4ed8",
    boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.14)",
  },
  searchKeyboard: {
    width: "100%",
    marginTop: "8px",
    padding: "10px",
    border: "1px solid #cbd5e1",
    borderRadius: "10px",
    backgroundColor: "#e8edf3",
    boxShadow: "0 8px 20px rgba(15, 23, 42, 0.14)",
    boxSizing: "border-box",
  },
  searchKeyboardRow: {
    display: "flex",
    justifyContent: "center",
    gap: "6px",
    marginBottom: "6px",
  },
  searchKeyboardKey: {
    flex: "1 1 0",
    minWidth: "32px",
    maxWidth: "62px",
    height: "40px",
    padding: "4px",
    border: "1px solid #b8c2cf",
    borderRadius: "7px",
    backgroundColor: "#fff",
    color: "#26384d",
    boxShadow: "0 2px 0 #aeb8c5",
    fontSize: "16px",
    fontWeight: "800",
    textTransform: "uppercase",
  },
  searchKeyboardActionRow: {
    display: "flex",
    gap: "6px",
  },
  searchKeyboardActionKey: {
    flex: "1 1 80px",
    minHeight: "40px",
    padding: "6px 10px",
    border: "1px solid #aeb8c5",
    borderRadius: "7px",
    backgroundColor: "#fff",
    color: "#334155",
    fontSize: "13px",
    fontWeight: "800",
  },
  searchKeyboardCloseKey: {
    flex: "1 1 80px",
    minHeight: "40px",
    padding: "6px 10px",
    border: "1px solid #2563eb",
    borderRadius: "7px",
    backgroundColor: "#2f80ed",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "800",
  },
  subTitle: {
    margin: "16px 0 12px 0",
    fontSize: "16px",
    color: "#34495e",
  },
  promotionSection: {
    marginBottom: "24px",
  },
  promotionSelect: {
    width: "100%",
    padding: "12px",
    fontSize: "16px",
    border: "2px solid #3498db",
    borderRadius: "6px",
    backgroundColor: "#fff",
  },
  filterSection: {
    display: "flex",
    gap: "12px",
    marginBottom: "20px",
    padding: "0 4px",
  },
  filterButton: {
    flex: 1,
    padding: "12px 20px",
    fontSize: "16px",
    fontWeight: "600",
    borderRadius: "8px",
    transition: "all 0.2s ease",
  },
  promoProducts: {
    marginBottom: "24px",
  },
  productGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
    gridAutoRows: "1fr",
    alignItems: "stretch",
    gap: "12px",
  },
  emptyProductSearch: {
    gridColumn: "1 / -1",
    padding: "28px 16px",
    border: "1px dashed #cbd5e1",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    color: "#64748b",
    textAlign: "center",
    fontSize: "15px",
    fontWeight: "700",
  },
  promoCard: {
    border: "2px solid #3498db",
    borderRadius: "8px",
    padding: "10px",
    cursor: "pointer",
    transition: "all 0.2s",
    minHeight: "118px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxSizing: "border-box",
  },
  promoCardColumnOdd: {
    backgroundColor: "#eef8ff",
  },
  promoCardColumnEven: {
    backgroundColor: "#e4f0ff",
  },
  promoCardSelected: {
    borderColor: "#27ae60",
    backgroundColor: "#e8f5e9",
  },
  promoCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "6px",
    fontSize: "18px",
    minHeight: "44px",
  },
  promoCardName: {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    lineHeight: "22px",
  },
  promoCardPrice: {
    fontSize: "15px",
    fontWeight: "bold",
    color: "#e74c3c",
    marginTop: "4px",
  },
  selectedQuantityBadge: {
    marginTop: "6px",
    padding: "4px 8px",
    borderRadius: "6px",
    backgroundColor: "#27ae60",
    color: "#fff",
    fontSize: "13px",
    fontWeight: "700",
    textAlign: "center",
  },
  selectedQuantityBadgeHidden: {
    visibility: "hidden",
  },
  regularProducts: {
    marginTop: "24px",
  },
  productCard: {
    border: "1px solid #ddd",
    borderRadius: "8px",
    padding: "16px",
    backgroundColor: "#fff",
    cursor: "pointer",
    transition: "all 0.2s",
    textAlign: "center",
  },
  productName: {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "8px",
  },
  productPrice: {
    fontSize: "16px",
    color: "#e74c3c",
    fontWeight: "bold",
  },
  billContent: {
    flex: 1,
    overflowY: "auto",
    padding: "20px",
  },
  emptyBill: {
    textAlign: "center",
    color: "#999",
    padding: "40px 20px",
    fontSize: "16px",
  },
  billItem: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px",
    marginBottom: "8px",
    backgroundColor: "#fff",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
  },
  billItemPromo: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    padding: "12px",
    marginBottom: "8px",
    backgroundColor: "#e8f5e9",
    borderRadius: "6px",
    border: "1px solid #81c784",
  },
  billItemAddon: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: "10px",
    padding: "10px 12px 10px 28px",
    margin: "-2px 0 8px 16px",
    backgroundColor: "#fff8e1",
    borderRadius: "6px",
    border: "1px solid #ffcc80",
  },
  billItemInfo: {
    flex: 1,
    minWidth: 0,
  },
  billItemName: {
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "4px",
    lineHeight: "18px",
    overflowWrap: "anywhere",
  },
  billItemPrice: {
    fontSize: "13px",
    color: "#666",
  },
  billItemActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "8px",
    flex: "0 0 auto",
  },
  quantityControlGroup: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "3px",
    borderRadius: "8px",
    backgroundColor: "#f6f8fb",
    border: "1px solid #dfe5ec",
  },
  billItemCommandRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: "6px",
  },
  btnMinus: {
    width: "30px",
    height: "30px",
    border: "1px solid #d4dbe4",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#26384d",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "700",
    lineHeight: "1",
  },
  btnPlus: {
    width: "30px",
    height: "30px",
    border: "1px solid #d4dbe4",
    borderRadius: "6px",
    backgroundColor: "#fff",
    color: "#26384d",
    cursor: "pointer",
    fontSize: "16px",
    fontWeight: "700",
    lineHeight: "1",
  },
  billQuantityInput: {
    width: "42px",
    height: "30px",
    padding: "4px",
    textAlign: "center",
    border: "1px solid #d4dbe4",
    borderRadius: "6px",
    fontWeight: "700",
    color: "#26384d",
  },
  addonButton: {
    minHeight: "30px",
    padding: "4px 10px",
    border: "1px solid #2f80ed",
    borderRadius: "6px",
    backgroundColor: "#eef6ff",
    color: "#1d5fbf",
    fontSize: "12px",
    fontWeight: "700",
    lineHeight: "18px",
  },
  removeItemButton: {
    width: "30px",
    height: "30px",
    padding: 0,
    border: "1px solid #f1b4ad",
    borderRadius: "6px",
    backgroundColor: "#fff1f0",
    color: "#d93025",
    fontSize: "20px",
    fontWeight: "700",
    lineHeight: "1",
  },
  btnRemove: {
    width: "28px",
    height: "28px",
    border: "1px solid #e74c3c",
    borderRadius: "4px",
    backgroundColor: "#fff",
    color: "#e74c3c",
    cursor: "pointer",
    fontSize: "18px",
    marginLeft: "4px",
  },
  promoDivider: {
    textAlign: "center",
    color: "#2e7d32",
    fontSize: "12px",
    padding: "8px 0",
    fontWeight: "500",
  },
  totalSection: {
    padding: "16px 18px 18px",
    borderTop: "1px solid #dfe5ec",
    backgroundColor: "#fff",
    boxShadow: "0 -4px 12px rgba(0,0,0,0.04)",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    fontSize: "18px",
    fontWeight: "bold",
  },
  totalLabel: {
    color: "#34495e",
    fontSize: "15px",
    fontWeight: "700",
  },
  totalAmount: {
    color: "#e74c3c",
    fontSize: "26px",
    lineHeight: "32px",
    whiteSpace: "nowrap",
  },
  drinkSelectionStatus: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginTop: "12px",
    padding: "10px 12px",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  drinkSelectionLabel: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "700",
  },
  drinkSelectionReady: {
    marginTop: "2px",
    color: "#2e7d32",
    fontSize: "13px",
    fontWeight: "700",
    lineHeight: "18px",
  },
  drinkSelectionPending: {
    marginTop: "2px",
    color: "#856404",
    fontSize: "13px",
    fontWeight: "700",
    lineHeight: "18px",
  },
  drinkSelectionBadgeReady: {
    minWidth: "58px",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: "800",
  },
  drinkSelectionBadgePending: {
    minWidth: "58px",
    padding: "6px 10px",
    borderRadius: "999px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    textAlign: "center",
    fontSize: "13px",
    fontWeight: "800",
  },
  actionButtons: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginTop: "14px",
  },
  secondaryActionRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  billSecondaryButton: {
    minHeight: "42px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "7px",
    padding: "8px 10px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    color: "#34495e",
    fontSize: "14px",
    fontWeight: "700",
  },
  billSecondaryButtonActive: {
    borderColor: "#2f80ed",
    backgroundColor: "#eef6ff",
    color: "#1d5fbf",
  },
  billButtonIcon: {
    fontSize: "15px",
    lineHeight: "1",
  },
  billButtonDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  checkoutButton: {
    width: "100%",
    minHeight: "58px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    padding: "11px 14px",
    border: "none",
    borderRadius: "8px",
    color: "#fff",
    boxShadow: "0 8px 18px rgba(39, 174, 96, 0.22)",
  },
  checkoutButtonReady: {
    backgroundColor: "#27ae60",
  },
  checkoutButtonPending: {
    backgroundColor: "#2f80ed",
  },
  checkoutButtonDisabled: {
    backgroundColor: "#94a3b8",
    boxShadow: "none",
    cursor: "not-allowed",
    opacity: 0.76,
  },
  checkoutButtonMain: {
    fontSize: "16px",
    fontWeight: "800",
    lineHeight: "20px",
  },
  checkoutButtonSub: {
    fontSize: "13px",
    fontWeight: "700",
    lineHeight: "18px",
    opacity: 0.9,
    whiteSpace: "nowrap",
  },
  modalClose: {
    border: "none",
    background: "none",
    fontSize: "28px",
    color: "#95a5a6",
    cursor: "pointer",
    padding: "0",
    width: "36px",
    height: "36px",
    lineHeight: "36px",
    borderRadius: "50%",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  btnModalCancel: {
    padding: "12px 24px",
    fontSize: "15px",
    backgroundColor: "#95a5a6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "600",
    transition: "all 0.2s ease",
  },
  btnModalConfirm: {
    padding: "12px 24px",
    fontSize: "15px",
    backgroundColor: "#27ae60",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    transition: "all 0.2s ease",
  },
  btnModalConfirmDisabled: {
    backgroundColor: "#bdc3c7",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  btnModalConfirmPayment: {
    padding: "14px 32px",
    fontSize: "16px",
    backgroundColor: "#27ae60",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontWeight: "bold",
    minWidth: "180px",
    transition: "all 0.2s ease",
  },
  btnModalConfirmPaymentDisabled: {
    backgroundColor: "#bdc3c7",
    cursor: "not-allowed",
    opacity: 0.6,
  },
  btnRemoveItem: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    border: "none",
    backgroundColor: "#e74c3c",
    color: "#fff",
    fontSize: "20px",
    fontWeight: "bold",
    lineHeight: "32px",
    textAlign: "center",
    cursor: "pointer",
    padding: 0,
    transition: "all 0.2s ease",
  },
  loading: {
    padding: "12px 24px",
    backgroundColor: "#fff3cd",
    color: "#856404",
    borderBottom: "1px solid #ffeaa7",
  },
  error: {
    padding: "12px 24px",
    backgroundColor: "#f8d7da",
    color: "#721c24",
    borderBottom: "1px solid #f5c6cb",
  },
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(15, 23, 42, 0.52)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "20px",
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "680px",
    maxHeight: "86vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.24)",
  },
  selectionModalContent: {
    maxWidth: "720px",
  },
  modalHeader: {
    padding: "16px 18px",
    borderBottom: "1px solid #e5eaf0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#f8fafc",
  },
  modalHeroHeader: {
    padding: "16px 18px",
    borderBottom: "1px solid #e5eaf0",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    backgroundColor: "#f8fafc",
  },
  modalTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  modalIconBox: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#eef6ff",
    border: "1px solid #d6e9ff",
    fontSize: "20px",
    flex: "0 0 auto",
  },
  modalKicker: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "700",
    lineHeight: "16px",
  },
  modalTitle: {
    margin: 0,
    fontSize: "20px",
    color: "#2c3e50",
    fontWeight: "800",
    lineHeight: "26px",
  },
  modalCloseButton: {
    width: "34px",
    height: "34px",
    padding: 0,
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#64748b",
    fontSize: "20px",
    fontWeight: "700",
    lineHeight: "1",
    flex: "0 0 auto",
  },
  modalBody: {
    padding: "18px",
    overflowY: "auto",
    flex: 1,
    backgroundColor: "#fff",
  },
  modalText: {
    fontSize: "15px",
    color: "#555",
    marginBottom: "12px",
  },
  warningModalMessage: {
    padding: "10px 2px",
    color: "#26384d",
    fontSize: "17px",
    fontWeight: "700",
    lineHeight: "26px",
    textAlign: "center",
  },
  selectionSummaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "10px",
    marginBottom: "14px",
  },
  selectionSummaryCard: {
    padding: "10px 12px",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
  },
  selectionSummaryCardReady: {
    backgroundColor: "#e8f5e9",
    borderColor: "#b8dfc0",
  },
  selectionSummaryLabel: {
    display: "block",
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "700",
    lineHeight: "16px",
  },
  selectionSummaryValue: {
    display: "block",
    color: "#26384d",
    fontSize: "22px",
    fontWeight: "800",
    lineHeight: "28px",
    marginTop: "2px",
  },
  drinkSummary: {
    display: "flex",
    justifyContent: "space-between",
    gap: "12px",
    padding: "12px",
    marginBottom: "12px",
    borderRadius: "8px",
    backgroundColor: "#f8f9fa",
    border: "1px solid #e0e0e0",
    fontWeight: "700",
    color: "#2c3e50",
  },
  drinkGridStack: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  drinkGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(158px, 1fr))",
    gridAutoRows: "1fr",
    alignItems: "stretch",
    gap: "10px",
  },
  drinkOption: {
    border: "2px solid #cbcccb",
    borderRadius: "8px",
    padding: "12px",
    cursor: "pointer",
    transition: "border-color 0.2s, box-shadow 0.2s, background-color 0.2s",
    textAlign: "left",
    backgroundColor: "#fff",
    minHeight: "132px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    boxSizing: "border-box",
  },
  drinkOptionSelected: {
    borderColor: "#27ae60",
    backgroundColor: "#f3fbf5",
    boxShadow: "0 0 0 2px rgba(39, 174, 96, 0.15)",
  },
  drinkOptionDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
  },
  drinkOptionTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  drinkSelectedBadge: {
    minWidth: "34px",
    padding: "3px 7px",
    borderRadius: "999px",
    backgroundColor: "#27ae60",
    color: "#fff",
    fontSize: "12px",
    fontWeight: "800",
    textAlign: "center",
  },
  drinkName: {
    fontSize: "18px",
    fontWeight: "700",
    marginBottom: "4px",
    color: "#26384d",
    lineHeight: "19px",
    overflowWrap: "anywhere",
  },
  drinkCode: {
    fontSize: "14px",
    color: "#028809",
    fontWeight: "700",
  },
  drinkQuantityControls: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "8px",
    marginTop: "12px",
  },
  drinkQuantityInput: {
    width: "48px",
    height: "30px",
    padding: "4px",
    textAlign: "center",
    border: "1px solid #d4dbe4",
    borderRadius: "6px",
    fontWeight: "700",
    color: "#26384d",
  },
  paymentModalContent: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "560px",
    maxHeight: "88vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.24)",
  },
  paymentTotalDisplay: {
    padding: "14px",
    backgroundColor: "#f8fafc",
    borderRadius: "8px",
    marginBottom: "14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    border: "1px solid #e2e8f0",
  },
  paymentTotalLabel: {
    display: "block",
    fontSize: "13px",
    color: "#64748b",
    fontWeight: "700",
    marginBottom: "2px",
  },
  paymentTotalAmount: {
    display: "block",
    fontSize: "30px",
    color: "#e74c3c",
    fontWeight: "800",
    lineHeight: "36px",
    whiteSpace: "nowrap",
  },
  paymentTotalBadge: {
    padding: "7px 10px",
    borderRadius: "999px",
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
    fontSize: "13px",
    fontWeight: "800",
    whiteSpace: "nowrap",
  },
  paymentFormGroup: {
    marginBottom: "20px",
  },
  paymentSection: {
    marginTop: "14px",
  },
  paymentSectionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px",
    marginBottom: "8px",
  },
  paymentSectionTitle: {
    display: "block",
    color: "#26384d",
    fontSize: "14px",
    fontWeight: "800",
    marginBottom: "8px",
  },
  paymentHint: {
    color: "#64748b",
    fontSize: "12px",
    fontWeight: "700",
    whiteSpace: "nowrap",
  },
  paymentMethodGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "8px",
  },
  paymentMethodButton: {
    minHeight: "58px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: "4px",
    padding: "8px 6px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#34495e",
  },
  paymentMethodButtonActive: {
    borderColor: "#27ae60",
    backgroundColor: "#f3fbf5",
    boxShadow: "0 0 0 2px rgba(39, 174, 96, 0.14)",
  },
  paymentMethodIcon: {
    fontSize: "18px",
    lineHeight: "20px",
  },
  paymentMethodLabel: {
    fontSize: "12px",
    fontWeight: "800",
    lineHeight: "15px",
    textAlign: "center",
  },
  paymentLabel: {
    display: "block",
    fontSize: "15px",
    marginBottom: "8px",
    color: "#2c3e50",
    fontWeight: "600",
  },
  paymentSelectModal: {
    width: "100%",
    padding: "12px",
    fontSize: "15px",
    border: "2px solid #ddd",
    borderRadius: "8px",
    backgroundColor: "#fff",
    boxSizing: "border-box",
    cursor: "pointer",
  },
  paymentInput: {
    width: "100%",
    height: "44px",
    padding: "10px 12px",
    fontSize: "16px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
    fontWeight: "700",
    color: "#26384d",
  },
  cashQuickRow: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "8px",
    marginTop: "8px",
  },
  cashQuickButton: {
    minHeight: "34px",
    padding: "6px 8px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#f8fafc",
    color: "#34495e",
    fontSize: "12px",
    fontWeight: "800",
  },
  cashQuickButtonActive: {
    borderColor: "#27ae60",
    backgroundColor: "#e8f5e9",
    color: "#2e7d32",
  },
  paymentTextarea: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    resize: "vertical",
    fontFamily: "inherit",
    boxSizing: "border-box",
    transition: "border-color 0.2s",
  },
  paymentToggleLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#64748b",
    fontSize: "13px",
    fontWeight: "700",
    margin: 0,
    whiteSpace: "nowrap",
  },
  paymentStatusSuccess: {
    marginTop: "8px",
    padding: "9px 10px",
    backgroundColor: "#e8f5e9",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#2e7d32",
    fontWeight: "700",
    border: "1px solid #81c784",
  },
  paymentStatusWarning: {
    marginTop: "8px",
    padding: "9px 10px",
    backgroundColor: "#fff3cd",
    borderRadius: "8px",
    fontSize: "14px",
    color: "#856404",
    fontWeight: "700",
    border: "1px solid #ffeaa7",
  },
  transferQRButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    padding: "12px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#fff",
    cursor: "zoom-in",
  },
  transferQRCode: {
    width: "200px",
    height: "200px",
    objectFit: "contain",
  },
  qrZoomOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 1100,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    backgroundColor: "rgba(15, 23, 42, 0.72)",
  },
  qrZoomContent: {
    position: "relative",
    padding: "22px",
    borderRadius: "12px",
    backgroundColor: "#fff",
    boxShadow: "0 18px 48px rgba(15, 23, 42, 0.28)",
  },
  qrZoomCloseButton: {
    position: "absolute",
    top: "8px",
    right: "8px",
    width: "34px",
    height: "34px",
    padding: 0,
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#64748b",
    fontSize: "20px",
    fontWeight: "700",
    lineHeight: "1",
  },
  transferQRCodeLarge: {
    width: "min(92vw, 620px)",
    height: "min(92vw, 620px)",
    objectFit: "contain",
    display: "block",
  },
  modalFooter: {
    padding: "14px 18px",
    borderTop: "1px solid #e5eaf0",
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    backgroundColor: "#f8fafc",
  },
  modalSecondaryButton: {
    minHeight: "42px",
    minWidth: "96px",
    padding: "8px 14px",
    border: "1px solid #d0d7de",
    borderRadius: "8px",
    backgroundColor: "#fff",
    color: "#34495e",
    fontSize: "14px",
    fontWeight: "800",
  },
  modalPrimaryButton: {
    minHeight: "42px",
    minWidth: "170px",
    padding: "8px 16px",
    border: "none",
    borderRadius: "8px",
    backgroundColor: "#27ae60",
    color: "#fff",
    fontSize: "14px",
    fontWeight: "800",
    boxShadow: "0 8px 18px rgba(39, 174, 96, 0.22)",
  },
  modalPrimaryButtonDisabled: {
    backgroundColor: "#94a3b8",
    boxShadow: "none",
    cursor: "not-allowed",
    opacity: 0.76,
  },
};

export default UuDaiCT;
